import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Activity, Bot, Headphones, Laptop, ShieldCheck, LineChart, Sparkles, Clock3, Globe2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { BRAND } from "../lib/brand";
import { Button } from "../components/ui/button";

const serviceIcons = [Activity, Bot, Headphones, Laptop, ShieldCheck];

const HomePage = () => {
  const { t } = useI18n();
  const stats = t("home.stats");
  const pillars = t("home.pillars.items");
  const steps = t("home.process.steps");

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-mesh opacity-50 pointer-events-none" />
        <div className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-brand-pastel/40 blur-3xl pointer-events-none dark:bg-brand-cyan/10" />
        <div className="absolute -bottom-40 -left-24 h-[420px] w-[420px] rounded-full bg-brand-cyan/20 blur-3xl pointer-events-none" />

        <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12 pt-16 md:pt-24 pb-20 md:pb-28 grid gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono-accent text-[11px] text-brand-navy/60 dark:text-brand-cyan reveal">
              {t("home.overline")}
            </div>
            <h1 className="mt-5 font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl reveal">
              {t("home.title1")}
              <span className="block">
                <span className="relative inline-block">
                  <span className="relative z-10">{t("home.title2").split(" ").slice(0, 3).join(" ")}</span>
                  <span className="absolute inset-x-0 bottom-1 h-3 bg-brand-cyan/40 dark:bg-brand-cyan/30 -z-0" />
                </span>{" "}
                {t("home.title2").split(" ").slice(3).join(" ")}
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed reveal" style={{ animationDelay: "120ms" }}>
              {t("home.sub")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3 reveal" style={{ animationDelay: "220ms" }}>
              <Link to="/contact" data-testid="hero-primary-cta">
                <Button
                  size="lg"
                  className="rounded-sm font-mono-accent text-xs bg-brand-navy text-white hover:bg-brand-cyan hover:text-brand-navy dark:bg-brand-cyan dark:text-brand-navy dark:hover:bg-white btn-press"
                >
                  {t("home.ctaPrimary")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/services" data-testid="hero-secondary-cta">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-sm font-mono-accent text-xs border-brand-navy/30 hover:border-brand-cyan hover:text-brand-cyan dark:border-brand-cyan/30 btn-press"
                >
                  {t("home.ctaSecondary")}
                </Button>
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-border pt-8 reveal" style={{ animationDelay: "320ms" }}>
              {stats.map((s, i) => (
                <div key={i} data-testid={`hero-stat-${i}`}>
                  <div className="font-display text-2xl md:text-3xl font-extrabold text-foreground">
                    {s.k}
                  </div>
                  <div className="mt-1 font-mono-accent text-[10px] text-muted-foreground">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 reveal" style={{ animationDelay: "180ms" }}>
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-br from-brand-cyan/30 via-transparent to-brand-pastel/40 blur-2xl rounded-sm dark:from-brand-cyan/20 dark:to-brand-cyan/5" />
              <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-brand-navy ring-1 ring-brand-cyan/30 neon-border">
                <img
                  src={BRAND.logo}
                  alt="Mato Systems logo"
                  className="absolute inset-0 h-full w-full object-cover opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 via-transparent to-transparent" />
                <div className="absolute left-5 right-5 bottom-5 flex items-end justify-between gap-4">

                  <div className="h-3 w-3 rounded-full bg-brand-cyan animate-glow-pulse" />
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 hidden sm:block">

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative py-24 md:py-32" data-testid="home-pillars-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div className="max-w-2xl">
              
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold tracking-tight">
                {t("home.pillars.title")}
              </h2>
              <p className="mt-4 text-muted-foreground md:text-lg">
                {t("home.pillars.sub")}
              </p>
            </div>
            <Link
              to="/services"
              data-testid="pillars-view-all-link"
              className="font-mono-accent text-[11px] inline-flex items-center gap-2 hover:text-brand-cyan"
            >
              View all services <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="grid gap-4 md:gap-5 grid-cols-1 md:grid-cols-6">
            {pillars.map((p, i) => {
              const Icon = serviceIcons[i % serviceIcons.length];
              const isWide = i === 0 || i === 3;
              return (
                <article
                  key={p.tag}
                  data-testid={`pillar-card-${i}`}
                  className={`group relative overflow-hidden rounded-sm glass-card p-6 md:p-8 transition duration-300 hover:-translate-y-1 ${isWide ? "md:col-span-4" : "md:col-span-2"
                    } ${i === 4 ? "md:col-span-6" : ""}`}
                >
                  <div className="absolute inset-0 dot-mesh opacity-60 pointer-events-none" />
                  <div className="relative flex items-start justify-between gap-4">
                    <div>
                      <div className="font-mono-accent text-[10px] text-brand-cyan">{p.tag}</div>
                      <h3 className="mt-2 font-display text-xl md:text-2xl font-semibold">
                        {p.title}
                      </h3>
                      <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-xl leading-relaxed">
                        {p.desc}
                      </p>
                    </div>
                    <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-brand-navy text-brand-cyan ring-1 ring-brand-cyan/30">
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative py-24 md:py-32 bg-brand-navy text-white" data-testid="home-process-section">
        <div className="absolute inset-0 grid-mesh opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="max-w-2xl">
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold tracking-tight">
              {t("home.process.title")}
            </h2>
            <p className="mt-4 text-white/70 md:text-lg">{t("home.process.sub")}</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <div
                key={s.n}
                data-testid={`process-step-${i}`}
                className="relative rounded-sm border border-brand-cyan/20 bg-black/40 backdrop-blur-sm p-6 md:p-7 transition hover:-translate-y-1 hover:border-brand-cyan"
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono-accent text-brand-cyan text-sm">{s.n}</div>
                  <div className="h-px flex-1 ml-4 bg-gradient-to-r from-brand-cyan/40 to-transparent" />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32" data-testid="home-cta-section">
        <div className="mx-auto max-w-5xl px-6 md:px-10 lg:px-12">
          <div className="relative overflow-hidden rounded-sm glass-card p-8 md:p-14">
            <div className="absolute inset-0 dot-mesh opacity-70 pointer-events-none" />
            <div className="relative flex flex-col md:flex-row md:items-end justify-between gap-8">
              <div className="max-w-xl">
                <div className="font-mono-accent text-[11px] text-brand-cyan flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5" /> Ready when you are
                </div>
                <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold tracking-tight">
                  {t("home.cta.title")}
                </h2>
                <p className="mt-4 text-muted-foreground md:text-lg">
                  {t("home.cta.sub")}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-4 text-xs font-mono-accent text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5" /> Tier 1 · 2</span>
                  <span className="inline-flex items-center gap-1.5"><Globe2 className="h-3.5 w-3.5" /> EN · BG</span>
                  <span className="inline-flex items-center gap-1.5"><LineChart className="h-3.5 w-3.5" /> Jira · Zendesk · Salesforce</span>
                </div>
              </div>
              <Link to="/contact" data-testid="home-cta-btn">
                <Button
                  size="lg"
                  className="rounded-sm font-mono-accent text-xs bg-brand-navy text-white hover:bg-brand-cyan hover:text-brand-navy dark:bg-brand-cyan dark:text-brand-navy dark:hover:bg-white btn-press"
                >
                  {t("home.cta.btn")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
